import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

const GROUPS = [
  { title: "Contact Details", fields: [
    ["phone", "Telephone"], ["whatsapp", "WhatsApp"], ["email", "Public email"],
    ["contact_email", "Enquiry notification email (staff)"], ["office", "Office / location"], ["hours", "Opening hours"],
  ]},
  { title: "Homepage & SEO", fields: [
    ["homepage_notice", "Homepage announcement notice"], ["footer_text", "Footer tagline"],
    ["seo_title", "Default SEO title"], ["seo_desc", "Default SEO description", true],
  ]},
  { title: "Regulatory (admin only)", fields: [
    ["iaa_registration_number", "IAA registration number"], ["iaa_badge_embed", "IAA badge embed / URL"],
    ["regulatory_text", "Regulatory statement", true],
  ]},
];
const SOCIAL = [["linkedin", "LinkedIn"], ["facebook", "Facebook"], ["youtube", "YouTube"], ["instagram", "Instagram"]];

export default function SettingsManager() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data || {})).catch(() => setS({})); }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setSocial = (k, v) => setS((p) => ({ ...p, social: { ...(p.social || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="max-w-3xl">
      <h1 className="font-serif text-3xl font-bold text-navy">Website Settings</h1>
      <p className="mt-1 text-sm text-slate-500">Manage contact details, homepage notice, regulatory information and default SEO.</p>

      <div className="mt-6 space-y-6">
        {GROUPS.map((g) => (
          <div key={g.title} className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-4 font-serif text-xl font-semibold text-navy">{g.title}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {g.fields.map(([k, label, isArea]) => (
                <div key={k} className={isArea ? "sm:col-span-2" : ""}>
                  <Label>{label}</Label>
                  {isArea ? (
                    <Textarea value={s[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1.5" rows={3} data-testid={`setting-${k}`} />
                  ) : (
                    <Input value={s[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1.5" data-testid={`setting-${k}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="mb-4 font-serif text-xl font-semibold text-navy">Social Links</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {SOCIAL.map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input value={s.social?.[k] || ""} onChange={(e) => setSocial(k, e.target.value)} className="mt-1.5" placeholder="https://…" data-testid={`setting-social-${k}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-gold disabled:opacity-60" data-testid="settings-save-btn">{saving ? "Saving…" : "Save Settings"}</button>
      </div>
    </div>
  );
}
